import { inject, injectable } from '../../modules/common/di';
import { FirmwareApi } from '../api/admin/firmware/firmwareApi';
import { AuthService } from './authService';
import { BaseService } from './baseService';
import { ApiResponseBase } from '../models/apiResponseBase';
import { GetFirmwareGroupsApiResponse } from '../api/admin/firmware/getFirmwareGroupsApiResponse';
import { GetFirmwareUpdateJobsApiResponse } from '../api/admin/firmware/getFirmwareUpdateJobsApiResponse';
import { GetFirmwareVersionsApiResponse } from '../api/admin/firmware/getFirmwareVersionsApiResponse';

@injectable('FirmwareService')
export class FirmwareService extends BaseService {
  @inject('FirmwareApi') private readonly firmwareApi!: FirmwareApi;
  @inject('AuthService') private readonly authService!: AuthService;

  constructor() {
    super();
  }

  /**
   * Get firmware update groups.
   * @param {number} [organizationId] Organization ID.
   * @returns {Promise<GetFirmwareGroupsApiResponse>}
   */
  public getFirmwareGroups(organizationId?: number): Promise<GetFirmwareGroupsApiResponse> {
    return this.authService.ensureAuthenticated()
      .then(() => this.firmwareApi.getFirmwareGroups(organizationId));
  }

  /**
   * Update or clear the firmware update group ID for a specific device.
   * @param params Requested params.
   * @param {number} params.deviceId Device instance ID.
   * @param {number} [params.groupId] Firmware group ID.
   * @returns {Promise<ApiResponseBase>}
   */
  public updateDeviceFirmwareGroup(params: {
    deviceId: number;
    groupId?: number;
  }): Promise<ApiResponseBase> {
    return this.authService.ensureAuthenticated()
      .then(() => this.firmwareApi.updateFirmwareGroup(params));
  }

  /**
   * Get firmware versions for specific device type.
   * @param {number} deviceType Device type.
   * @returns {Promise<GetFirmwareVersionsApiResponse>}
   */
  public getFirmwareVersions(deviceType: number): Promise<GetFirmwareVersionsApiResponse> {
    if (deviceType < 1 || isNaN(deviceType)) {
      return this.reject(`Device type is incorrect [${deviceType}].`);
    }
    return this.authService.ensureAuthenticated()
      .then(() => this.firmwareApi.getFirmwareVersions(deviceType));
  }

  /**
   * Get firmware update jobs for specified device type.
   * @param {number} deviceType Device type.
   * @param {number} [groupId] Firmware update group ID.
   * @returns {Promise<GetFirmwareUpdateJobsApiResponse>}
   */
  public getFirmwareUpdateJobs(deviceType: number, groupId?: number): Promise<GetFirmwareUpdateJobsApiResponse> {
    if (deviceType < 1 || isNaN(deviceType)) {
      return this.reject(`Device type is incorrect [${deviceType}].`);
    }
    const params: {
      deviceType: number;
      groupId?: number;
    } = {
      deviceType: deviceType,
    };

    if (groupId && !isNaN(groupId)) {
      params.groupId = groupId;
    }

    return this.authService.ensureAuthenticated()
      .then(() => this.firmwareApi.getFirmwareUpdateJob(params));
  }

}
